import React from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, BookOpen, Eye, CreditCard, TrendingUp,
  Crown, Clock, Trash2, Shield, ShieldOff, BarChart3,
  Link2, Percent, Euro, Plus, Copy, Check as CheckIcon, X,
  Ban, UserCheck, Gift, CalendarPlus, Download, History, MoreVertical, Mail, Send, Tag,
  ImageIcon, Wand2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { TabsContent } from '../../ui/tabs';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';

export const StoriesTab = ({ ctx }) => {
  const { approvedStories, token, API, setShowDeleteAllDialog } = ctx;
  const missingCoverCount = approvedStories.filter((s) => !s.cover_image_url).length;
  const [autoFixing, setAutoFixing] = React.useState(false);

  const handleAutoFixCovers = async () => {
    if (!window.confirm(`Probeer ${missingCoverCount} ontbrekende covers automatisch te koppelen aan bestaande illustraties?`)) return;
    setAutoFixing(true);
    try {
      const res = await axios.post(`${API}/stories/auto-fix-covers`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const { fixed_count, skipped_count, skipped } = res.data;
      if (fixed_count > 0) toast.success(`${fixed_count} cover(s) hersteld!`);
      if (skipped_count > 0) {
        const titles = (skipped || []).map((s) => s.title).slice(0, 5).join(', ');
        toast.info(`${skipped_count} verhalen niet gematcht: ${titles}`);
      }
      if (fixed_count === 0 && skipped_count === 0) toast.info('Geen verhalen zonder cover gevonden.');
      // refresh
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-fix mislukt');
    } finally {
      setAutoFixing(false);
    }
  };

  return (
  <TabsContent value="stories" className="space-y-6" data-testid="stories-tab-content">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <CardTitle className="font-display text-xl flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-gold" />
                      Alle Verhalen ({approvedStories.length})
                      {missingCoverCount > 0 && (
                        <span className="text-xs font-normal text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="missing-cover-count">
                          {missingCoverCount} zonder cover
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2">
                      {missingCoverCount > 0 && (
                        <Button
                          data-testid="auto-fix-covers-btn"
                          variant="outline"
                          size="sm"
                          onClick={handleAutoFixCovers}
                          disabled={autoFixing}
                          className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                        >
                          <Wand2 className="w-4 h-4" />
                          {autoFixing ? 'Bezig...' : 'Auto-fix covers'}
                        </Button>
                      )}
                      <Button
                        data-testid="delete-all-stories-btn2"
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowDeleteAllDialog(true)}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Alles verwijderen
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {approvedStories.length > 0 ? (
                    <div className="space-y-2">
                      {approvedStories.map((story) => (
                        <div key={story.id} className="flex items-center justify-between p-3 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors gap-2">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {story.cover_image_url ? (
                              <img src={story.cover_image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-amber-100 border border-amber-300 flex items-center justify-center shrink-0" title="Geen cover">
                                <ImageIcon className="w-4 h-4 text-amber-700" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{story.title}</p>
                              <p className="text-xs text-muted-foreground">{story.grandma_name || 'Onbekend'} · {story.pages?.length || 0} pag.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/upload?edit=${story.id}`, '_self')}
                              className="rounded-full h-7 text-xs px-2"
                            >
                              Bewerk
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (window.confirm(`"${story.title}" verwijderen?`)) {
                                  try {
                                    await axios.delete(`${API}/stories/${story.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success('Verhaal verwijderd');
                                    window.location.reload();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Verwijderen mislukt');
                                  }
                                }
                              }}
                              className="rounded-full text-destructive border-destructive/20 hover:bg-destructive/10 h-7 w-7 p-0"
                              data-testid={`delete-single-${story.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Geen verhalen</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
  );
};
